import { useMemo } from 'react'

import { CustomerType } from '@features/contacts/enums/customerType'
import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { ContactType } from '@features/contacts/types/contactType'
import { getIsAddressExemptionAvailable } from '@features/contacts/utils/getIsAddressExemptionAvailable'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { PredefinedCustomerValues } from '@features/invoices/types/predefinedCustomerValues'

import { useInvoiceEditorForm } from '../../contexts/invoiceEditorFormContext'
import { resolveEInvoiceConfig } from '../../utils/missingComplianceDetails/resolveEInvoiceConfig'

export const usePredefinedCustomerValues = () => {
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { watch } = invoiceEditorForm
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const receiver = watch('receiver')
  const {
    addressLine2,
    cityText,
    countryId,
    contactPersons,
    ean,
    eInvoiceScheme,
    id,
    isSalesTaxExempt,
    name,
    publicEntityRoutingEnabled,
    street,
    type,
    vatNo,
    zipcodeText,
  } = receiver || {}

  const { contact: receiverContact } = useFetchContactById(id, { enabled: isFrenchComplianceMode && !!id })

  const isAddressExempt = receiverContact?.isAddressExempt

  const primaryContact = contactPersons?.find((contactPerson) => contactPerson.isPrimary) || contactPersons?.[0]
  const { firstName, lastName } = primaryContact || {}
  const customerType = type === ContactType.Person ? CustomerType.Individual : CustomerType.Company
  const isCompany = customerType === CustomerType.Company
  const companyName = isCompany ? name : undefined
  const isAddressExemptionAvailable = getIsAddressExemptionAvailable({
    customerCountry: countryId,
    customerType,
    isFrenchComplianceMode,
  })

  const {
    eInvoiceAddress: resolvedEInvoiceAddress,
    entityType: resolvedEntityType,
    scheme: resolvedEInvoiceScheme,
  } = resolveEInvoiceConfig({ countryId, ean, eInvoiceScheme, publicEntityRoutingEnabled })

  const predefinedCustomerValues: PredefinedCustomerValues = useMemo(
    () => ({
      address: {
        country: countryId,
        city: cityText,
        pobox: zipcodeText,
        streetAddress1: street,
        streetAddress2: addressLine2 ?? '',
      },
      customerType,
      eInvoiceAddress: { address: resolvedEInvoiceAddress, scheme: resolvedEInvoiceScheme },
      entityType: resolvedEntityType,
      firstName,
      id,
      isAddressExempt: isAddressExemptionAvailable && !!isAddressExempt,
      isVatExempted: isSalesTaxExempt,
      lastName,
      vatNumber: vatNo,
      ...(isCompany ? { companyName } : { firstName, lastName }),
    }),
    [
      addressLine2,
      cityText,
      companyName,
      countryId,
      customerType,
      firstName,
      id,
      isAddressExempt,
      isAddressExemptionAvailable,
      isCompany,
      isSalesTaxExempt,
      lastName,
      resolvedEInvoiceAddress,
      resolvedEInvoiceScheme,
      resolvedEntityType,
      street,
      vatNo,
      zipcodeText,
    ],
  )

  return { predefinedCustomerValues }
}
